'use client'

/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/Darstellung.tsx
 *
 * Schalter „Dunkle Darstellung“ — Dunkelmodus nur auf Wunsch (FLEET_DESIGN_SPEC,
 * Schritt 4). Standard ist hell; dunkel heisst `<html data-theme="dark">`, die Wahl
 * liegt im Cookie `<app>_theme=dark` (ein Jahr, Path=/, SameSite=Lax, Secure unter
 * https), „hell“ löscht ihn. Das Root-Layout der App liest den Cookie serverseitig
 * und setzt das Attribut schon im ersten HTML — das ist App-Code (next/headers),
 * nicht Teil dieses Bausteins.
 *
 * Zwei Formen, ein Zustand:
 *  - `DarstellungKnopf`: runder Symbolknopf für den KOPF — auf jeder Seite, auch
 *    ohne Anmeldung, neben der Sprachwahl (Entscheidung 07.10.2026).
 *  - `DarstellungSchalter`: Knopf mit Beschriftung für die Einstellung
 *    „Darstellung“, optional mit Hilfetext.
 * Alle Exemplare einer Seite bleiben über ein Fenster-Ereignis synchron.
 * `beiAenderung` meldet jede Wahl der Person, z. B. um sie im Konto zu speichern,
 * damit sie auf allen Geräten gilt (Vokabero, Entscheidung 07.10.2026); der
 * Cookie ist dann schon geschrieben, die Seite schon umgeschaltet.
 *
 * Farben und Formen nur über die Pflicht-Rollen der Spec (`--bg-primary`,
 * `--text-secondary`, `--text-primary`, `--border`, `--primary`,
 * `--text-on-primary`, `--radius-pill`, `--radius-round`); Texte kommen als Props
 * (Regel #12).
 */
import { useSyncExternalStore } from 'react'

export const DARSTELLUNG_EREIGNIS = 'fundament:darstellung'
const DUNKEL = 'dark'
const EIN_JAHR = 60 * 60 * 24 * 365

export function istDunkel(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.theme === DUNKEL
}

export function setzeDunkel(cookie: string, dunkel: boolean): void {
  const sicher = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = dunkel
    ? `${cookie}=${DUNKEL}; Max-Age=${EIN_JAHR}; Path=/; SameSite=Lax${sicher}`
    : `${cookie}=; Max-Age=0; Path=/; SameSite=Lax${sicher}`
  if (dunkel) document.documentElement.dataset.theme = DUNKEL
  else delete document.documentElement.dataset.theme
  window.dispatchEvent(new CustomEvent(DARSTELLUNG_EREIGNIS, { detail: dunkel }))
}

function abonnieren(melden: () => void): () => void {
  window.addEventListener(DARSTELLUNG_EREIGNIS, melden)
  return () => window.removeEventListener(DARSTELLUNG_EREIGNIS, melden)
}

/**
 * Aktueller Stand und Umschalter; jede Komponente, die ihn nutzt, folgt jeder anderen.
 * `anfang` ist der Stand, mit dem der Server gerendert hat (das Root-Layout kennt den
 * Cookie) — dann springt das Symbol beim Hydrieren nicht vom Mond zur Sonne.
 */
export function useDunkel(cookie: string, anfang: boolean = false): [boolean, (dunkel: boolean) => void] {
  const dunkel = useSyncExternalStore(abonnieren, istDunkel, () => anfang)
  return [dunkel, (wert: boolean) => setzeDunkel(cookie, wert)]
}

function Mond() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  )
}

function Sonne() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  )
}

interface KnopfProps {
  /** Cookie der App, z. B. `paperball_theme`, `m10_theme`. */
  cookie: string
  /** „Dunkle Darstellung“ in der Sprache der Seite — Name und Tooltip des Knopfs. */
  beschriftung: string
  /** Stand beim Server-Rendern (Cookie gesetzt?), damit beim Hydrieren nichts springt. */
  anfang?: boolean
  /** Wird nach jeder Wahl aufgerufen (Cookie und Seite sind dann schon umgestellt). */
  beiAenderung?: (dunkel: boolean) => void
  className?: string
}

/**
 * Symbolknopf für den Kopf: Mond (hell aktiv) bzw. Sonne (dunkel aktiv). Trefffläche
 * 44 px (Apple HIG, WCAG 2.5.5) — `groesse` nur verkleinern, wo der Kopf es erzwingt.
 */
export function DarstellungKnopf({ cookie, beschriftung, anfang, beiAenderung, groesse = 44, className }: KnopfProps & { groesse?: number }) {
  const [dunkel, setzen] = useDunkel(cookie, anfang)
  return (
    <button
      type="button"
      className={className}
      aria-pressed={dunkel}
      aria-label={beschriftung}
      title={beschriftung}
      onClick={() => {
        setzen(!dunkel)
        beiAenderung?.(!dunkel)
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: groesse, height: groesse, flexShrink: 0, cursor: 'pointer',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
        background: 'var(--bg-primary)', color: 'var(--text-secondary)',
      }}
    >
      {dunkel ? <Sonne /> : <Mond />}
    </button>
  )
}

interface SchalterProps extends KnopfProps {
  /** Hilfetext darunter, z. B. „<App> ist hell, solange du nichts änderst. …“ */
  hilfe?: string
}

/** Knopf mit Beschriftung und Kippanzeige für die Einstellung „Darstellung“. */
export function DarstellungSchalter({ cookie, beschriftung, hilfe, anfang, beiAenderung, className }: SchalterProps) {
  const [dunkel, setzen] = useDunkel(cookie, anfang)
  return (
    <div className={className}>
      <button
        type="button"
        aria-pressed={dunkel}
        onClick={() => {
          setzen(!dunkel)
          beiAenderung?.(!dunkel)
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '6px 16px',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)',
          background: 'var(--bg-primary)', color: 'var(--text-primary)',
          fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Mond />
        {beschriftung}
        <span aria-hidden="true" style={{
          position: 'relative', width: 30, height: 18, borderRadius: 'var(--radius-pill)',
          border: '1px solid var(--border)', background: dunkel ? 'var(--primary)' : 'var(--bg-secondary)',
        }}>
          <span style={{
            position: 'absolute', top: 2, left: dunkel ? 14 : 2, width: 12, height: 12,
            borderRadius: 'var(--radius-round)',
            background: dunkel ? 'var(--text-on-primary)' : 'var(--text-secondary)',
          }} />
        </span>
      </button>
      {hilfe ? (
        <p style={{ margin: '10px 0 0', fontSize: '1rem', color: 'var(--text-secondary)' }}>{hilfe}</p>
      ) : null}
    </div>
  )
}
